import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/AuthContext";
import { api, API } from "@/lib/api";
import { Video, Link2, LayoutGrid, ArrowRight, Star, Sparkles, Check, Copy, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { LangSwitcher } from "@/components/LangSwitcher";
import { SeoHead } from "@/components/SeoHead";
import { StructuredData, ORG_SCHEMA, softwareAppSchema, faqSchema } from "@/components/StructuredData";

const PLATFORMS = [
  { name: "WordPress", slug: "wordpress" },
  { name: "Shopify", slug: "shopify" },
  { name: "Wix", slug: "wix" },
  { name: "Webflow", slug: "webflow" },
  { name: "Framer", slug: "framer" },
  { name: "Squarespace", slug: "squarespace" },
  { name: "Elementor", slug: "elementor" },
  { name: "Notion", slug: "notion" },
];

const EMBED_SNIPPET = `<iframe
  src="https://depoia.com.br/w/sua-marca"
  width="100%" height="640" frameborder="0"
  style="border:none;background:transparent"
></iframe>`;

export default function Landing() {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [showcase, setShowcase] = useState([]);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    api.get("/public/showcase?limit=12").then((r) => setShowcase(r.data.items || [])).catch(() => {});
  }, []);

  const copyEmbed = async () => {
    try {
      await navigator.clipboard.writeText(EMBED_SNIPPET);
      setCopied(true);
      toast.success(t("landing.embedCopied") + "!");
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast.error("Copy manually with Ctrl+C");
    }
  };

  const heroTitle = t("landing.heroTitle", { returnObjects: true });
  const howTitle = t("landing.howTitle", { returnObjects: true });
  const embedTitle = t("landing.embedTitle", { returnObjects: true });
  const embedFeatures = t("landing.embedFeatures", { returnObjects: true });

  return (
    <div className="min-h-screen bg-[#FAFAFA] overflow-x-hidden">
      <SeoHead pageKey="home" />
      <StructuredData data={ORG_SCHEMA} />
      <StructuredData data={softwareAppSchema(i18n.language?.startsWith("pt") ? "pt" : "en")} />
      <StructuredData data={faqSchema(
        i18n.language?.startsWith("pt")
          ? [
              { q: "Onde ficam hospedados os vídeos dos depoimentos?", a: "Todos os vídeos são armazenados em servidores seguros no Emergent Object Storage, com URLs privadas e controle total do dono da campanha." },
              { q: "O cliente precisa criar conta pra deixar depoimento?", a: "Não. O cliente abre o link único da campanha, escolhe vídeo/voz/texto e envia. Sem app, sem cadastro." },
              { q: "Os depoimentos ajudam meu SEO?", a: "Sim. O widget aparece dentro do site do cliente e, no plano Growth, injeta schema Review + AggregateRating para gerar rich snippet de estrelas no Google." },
              { q: "Funciona em qualquer site?", a: "Sim. Uma linha de iframe funciona em WordPress, Shopify, Wix, Webflow, Framer, Notion, Carrd, Bubble e HTML puro." },
              { q: "Quanto tempo leva pra instalar?", a: "Menos de 30 segundos. Cria a campanha, copia o snippet, cola no seu site." },
              { q: "O que acontece se eu cancelar?", a: "Nada é deletado. Os depoimentos ficam intactos e você pode exportar tudo em CSV a qualquer momento." },
            ]
          : [
              { q: "Where are testimonial videos hosted?", a: "All videos are stored on secure servers via Emergent Object Storage with private URLs and full owner control." },
              { q: "Does the customer need an account to leave a testimonial?", a: "No. Your customer opens the campaign's unique link, picks video/voice/text and submits. No app, no signup." },
              { q: "Do testimonials help my SEO?", a: "Yes. The widget renders inside your site and, on the Growth plan, injects Review + AggregateRating schema for star rich snippets on Google." },
              { q: "Does it work on any site?", a: "Yes. One iframe line works on WordPress, Shopify, Wix, Webflow, Framer, Notion, Carrd, Bubble and plain HTML." },
              { q: "How long does it take to install?", a: "Under 30 seconds. Create the campaign, copy the snippet, paste on your site." },
              { q: "What happens if I cancel?", a: "Nothing gets deleted. Testimonials stay intact and you can export everything as CSV any time." },
            ]
      )} />
      <header className="max-w-6xl mx-auto px-6 py-6 flex items-center justify-between">
        <span className="font-heading font-extrabold text-xl tracking-tight">depoia<span className="text-[#FF4F00]">.</span></span>
        <div className="flex items-center gap-3">
          <LangSwitcher />
          <Link data-testid="nav-pricing-link" to="/pricing" className="px-4 py-2.5 text-sm font-semibold text-zinc-600 hover:text-[#FF4F00] transition-colors">
            {t("nav.pricing")}
          </Link>
          {user ? (
            <Link data-testid="nav-dashboard-link" to="/dashboard" className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              {t("nav.dashboard")}
            </Link>
          ) : (
            <Link data-testid="nav-login-link" to="/login" className="px-5 py-2.5 bg-[#0A0A0A] text-white rounded-full text-sm font-semibold hover:bg-zinc-800 transition-colors">
              {t("nav.login")}
            </Link>
          )}
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-6xl mx-auto px-6 pt-14 pb-20 lg:pt-20">
        <div className="grid lg:grid-cols-[1.05fr_1fr] gap-14 items-center">
          <div>
            <div className="inline-flex items-center gap-2 bg-orange-50 border border-orange-100 rounded-full px-3 py-1 mb-6 fade-up">
              <Sparkles size={13} className="text-[#FF4F00]" />
              <span className="text-xs font-semibold text-[#FF4F00] uppercase tracking-wider">{t("landing.badge")}</span>
            </div>
            <h1 className="font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[1.02] fade-up">
              {heroTitle[0]}<br />
              <span className="text-[#FF4F00]">{heroTitle[1]}</span>{heroTitle[2]}<br />
              {heroTitle[3]}
            </h1>
            <p className="text-zinc-600 text-base sm:text-lg mt-7 max-w-lg fade-up" style={{ animationDelay: "0.1s" }}>
              {t("landing.heroSub")}
            </p>
            <div className="flex flex-wrap items-center gap-4 mt-9 fade-up" style={{ animationDelay: "0.2s" }}>
              <Link
                data-testid="hero-cta-btn"
                to={user ? "/dashboard" : "/login"}
                className="px-8 py-4 bg-[#FF4F00] text-white rounded-full font-semibold flex items-center gap-2 hover:bg-[#E64600] hover:-translate-y-0.5 transition-[background-color,transform] shadow-lg shadow-orange-500/20"
              >
                {t("landing.ctaStart")} <ArrowRight size={18} />
              </Link>
              <div className="text-sm text-zinc-500 flex items-center gap-2">
                <Check size={16} className="text-emerald-500" />
                {t("landing.ctaMinutes")}
              </div>
            </div>
          </div>
          <div className="relative fade-up" style={{ animationDelay: "0.3s" }}>
            <div className="absolute -inset-6 bg-gradient-to-tr from-orange-100/70 via-transparent to-transparent rounded-3xl blur-2xl" />
            <div className="relative rounded-2xl overflow-hidden border border-zinc-200 shadow-2xl shadow-zinc-900/10 bg-white">
              <div className="flex items-center gap-1.5 px-4 py-3 border-b border-zinc-100 bg-[#FAFAFA]">
                <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                <span className="text-[10px] text-zinc-400 ml-2 font-mono">depoia.com.br/dashboard</span>
              </div>
              <img src="/mockups/panel.jpg" alt="Painel depoia" className="w-full block" />
            </div>
            <div className="absolute -bottom-6 -right-4 md:-right-8 w-40 md:w-48 rounded-xl overflow-hidden shadow-2xl shadow-zinc-900/20 border-2 border-white rotate-3">
              <img src="/mockups/collect.jpg" alt="Página de coleta mobile" className="w-full block" />
            </div>
          </div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="border-y border-zinc-200 bg-white py-8 overflow-hidden">
        <p className="text-center text-xs uppercase tracking-widest text-zinc-400 font-semibold mb-5">{t("landing.trust")}</p>
        <div className="relative">
          <div className="flex gap-14 animate-marquee whitespace-nowrap">
            {[...PLATFORMS, ...PLATFORMS].map((p, i) => (
              <div key={i} className="flex items-center gap-2 shrink-0 text-zinc-500">
                <img
                  src={`https://cdn.simpleicons.org/${p.slug}/71717A`}
                  alt={p.name}
                  loading="lazy"
                  className="h-6 w-6 opacity-70"
                />
                <span className="font-heading font-semibold text-lg">{p.name}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* HOW IT WORKS - big mockups */}
      <section className="max-w-6xl mx-auto px-6 pt-24 pb-16">
        <div className="max-w-2xl mb-16">
          <p className="text-[#FF4F00] font-semibold text-xs uppercase tracking-widest mb-3">{t("landing.howKicker")}</p>
          <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight">
            {howTitle[0]} <span className="text-[#FF4F00]">{howTitle[1]}</span>{howTitle[2]}
          </h2>
        </div>

        {[
          { n: "01", title: t("landing.step1Title"), desc: t("landing.step1Desc"), img: "/mockups/dashboard.jpg", browser: "depoia.com.br/dashboard", reverse: false },
          { n: "02", title: t("landing.step2Title"), desc: t("landing.step2Desc"), img: "/mockups/collect.jpg", browser: "depoia.com.br/c/your-brand", reverse: true, mobileWidth: true },
          { n: "03", title: t("landing.step3Title"), desc: t("landing.step3Desc"), img: "/mockups/widget.jpg", browser: "yoursite.com", reverse: false },
        ].map((step) => (
          <div key={step.n} className={`grid lg:grid-cols-2 gap-12 items-center mb-20 last:mb-0 ${step.reverse ? "lg:[&>div:first-child]:order-2" : ""}`}>
            <div>
              <div className="text-[#FF4F00] font-mono font-bold text-lg mb-3">{step.n}</div>
              <h3 className="font-heading font-extrabold text-2xl sm:text-3xl tracking-tight leading-tight mb-4">{step.title}</h3>
              <p className="text-zinc-600 text-base sm:text-lg leading-relaxed">{step.desc}</p>
            </div>
            <div className={`relative ${step.mobileWidth ? "max-w-xs mx-auto" : ""}`}>
              <div className="absolute -inset-4 bg-gradient-to-br from-orange-50 to-transparent rounded-2xl -z-10" />
              <div className="rounded-xl overflow-hidden border border-zinc-200 shadow-xl bg-white">
                <div className="flex items-center gap-1.5 px-3.5 py-2.5 border-b border-zinc-100 bg-[#FAFAFA]">
                  <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
                  <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2 h-2 rounded-full bg-[#28C840]" />
                  <span className="text-[10px] text-zinc-400 ml-1.5 font-mono truncate">{step.browser}</span>
                </div>
                <img src={step.img} alt={step.title} loading="lazy" className="w-full block" />
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* EMBED SNIPPET */}
      <section data-testid="embed-section" className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid lg:grid-cols-[1fr_1.2fr] gap-12 items-center">
          <div>
            <p className="text-[#FF4F00] font-semibold text-xs uppercase tracking-widest mb-3">{t("landing.embedKicker")}</p>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight leading-tight mb-5">{embedTitle[0]} <span className="text-[#FF4F00]">{embedTitle[1]}</span>{embedTitle[2]}</h2>
            <p className="text-zinc-600 text-base sm:text-lg leading-relaxed mb-6">
              {t("landing.embedDesc")}
            </p>
            <ul className="space-y-2.5 text-zinc-700 text-sm">
              {embedFeatures.map((f) => (
                <li key={f} className="flex items-center gap-2.5"><Check size={16} className="text-emerald-500 shrink-0" /> {f}</li>
              ))}
            </ul>
          </div>
          <div>
            <div className="bg-[#0A0A0A] rounded-xl overflow-hidden shadow-2xl">
              <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                <div className="flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FF5F57]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#FEBC2E]" />
                  <span className="w-2.5 h-2.5 rounded-full bg-[#28C840]" />
                </div>
                <span className="text-[10px] font-mono text-zinc-500 uppercase tracking-wider">HTML</span>
                <button
                  data-testid="copy-embed-btn"
                  onClick={copyEmbed}
                  className="flex items-center gap-1.5 text-xs font-semibold text-zinc-300 hover:text-white transition-colors"
                >
                  {copied ? <><Check size={13} /> {t("landing.embedCopied")}</> : <><Copy size={13} /> {t("landing.embedCopy")}</>}
                </button>
              </div>
              <pre className="px-5 py-5 text-[13px] text-zinc-200 font-mono leading-relaxed overflow-x-auto"><code>{EMBED_SNIPPET}</code></pre>
            </div>
          </div>
        </div>
      </section>

      {/* SHOWCASE OF REAL AVATARS */}
      {showcase.length > 0 && (
        <section data-testid="showcase-section" className="max-w-6xl mx-auto px-6 py-20">
          <div className="flex items-baseline justify-between mb-10 gap-4 flex-wrap">
            <div>
              <p className="text-[#FF4F00] font-semibold text-xs uppercase tracking-widest mb-3">{t("landing.showcaseKicker")}</p>
              <h2 className="font-heading font-extrabold text-3xl sm:text-4xl tracking-tight max-w-2xl leading-tight">
                {t("landing.showcaseTitle")}
              </h2>
            </div>
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 sm:gap-4">
            {showcase.map((s) => (
              <div key={s.id} data-testid={`showcase-item-${s.id}`} className="group relative aspect-square rounded-lg overflow-hidden bg-zinc-100">
                <img
                  src={`${API}/testimonials/${s.id}/avatar`}
                  alt={s.author_name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/0 to-transparent opacity-0 group-hover:opacity-100 transition-opacity" />
                <div className="absolute bottom-0 left-0 right-0 p-2.5 text-white opacity-0 group-hover:opacity-100 transition-opacity">
                  <p className="font-bold text-xs truncate">{s.author_name}</p>
                  {s.business_name && <p className="text-[10px] text-zinc-300 truncate">via {s.business_name}</p>}
                  <div className="flex gap-0.5 mt-1">
                    {[...Array(s.rating || 5)].map((_, i) => <Star key={i} size={9} className="fill-[#FF4F00] text-[#FF4F00]" />)}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* FEATURE GRID (short 3 cards) */}
      <section className="max-w-6xl mx-auto px-6 py-20">
        <div className="grid md:grid-cols-3 gap-6">
          {[
            { icon: Video, title: t("landing.featureAiTitle"), desc: t("landing.featureAiDesc") },
            { icon: ShieldCheck, title: t("landing.featureLgpdTitle"), desc: t("landing.featureLgpdDesc") },
            { icon: LayoutGrid, title: t("landing.featureWidgetTitle"), desc: t("landing.featureWidgetDesc") },
          ].map((f, i) => (
            <div key={i} className="bg-white border border-zinc-200 rounded-xl p-7 hover:border-zinc-300 hover:-translate-y-0.5 transition-[transform,border-color]">
              <f.icon size={24} className="text-[#FF4F00] mb-5" />
              <h3 className="font-heading font-bold text-lg mb-2 tracking-tight">{f.title}</h3>
              <p className="text-sm text-zinc-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="max-w-6xl mx-auto px-6 pb-24">
        <div className="bg-[#0A0A0A] rounded-2xl p-10 sm:p-16 relative overflow-hidden">
          <div className="absolute -top-24 -right-24 w-96 h-96 bg-[#FF4F00] opacity-15 blur-3xl rounded-full" />
          <div className="relative">
            <div className="flex gap-1 mb-6">
              {[...Array(5)].map((_, i) => <Star key={i} size={20} className="fill-[#FF4F00] text-[#FF4F00]" />)}
            </div>
            <p className="font-heading font-light text-2xl sm:text-4xl text-white max-w-2xl tracking-tight leading-snug">
              {t("landing.ctaQuote")}
            </p>
            <p className="text-zinc-400 text-sm mt-6">{t("landing.ctaSub")}</p>
            <div className="flex flex-wrap gap-4 items-center mt-10">
              <Link
                data-testid="bottom-cta-btn"
                to={user ? "/dashboard" : "/login"}
                className="inline-flex items-center gap-2 px-8 py-4 bg-white text-[#0A0A0A] rounded-full font-semibold hover:bg-zinc-200 transition-colors"
              >
                {t("landing.ctaFinal")} <ArrowRight size={18} />
              </Link>
              <Link to="/pricing" className="text-sm font-semibold text-zinc-300 hover:text-white transition-colors">
                {t("landing.ctaSeePlans")}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <footer className="max-w-6xl mx-auto px-6 py-10 border-t border-zinc-200 flex flex-wrap items-center justify-between gap-4 text-sm text-zinc-400">
        <span>{t("landing.footerTag")}</span>
        <div className="flex gap-6">
          <Link data-testid="footer-integrations-link" to="/integrations" className="hover:text-zinc-700 transition-colors">{t("integrations.footerLink")}</Link>
          <Link data-testid="footer-privacy-link" to="/privacidade" className="hover:text-zinc-700 transition-colors">{t("landing.privacy")}</Link>
          <Link data-testid="footer-terms-link" to="/termos" className="hover:text-zinc-700 transition-colors">{t("landing.terms")}</Link>
        </div>
      </footer>
    </div>
  );
}
